import { useState } from 'react';
import PropTypes from 'prop-types';
import { useIntl, defineMessages, FormattedMessage } from 'dibs-react-intl';

import { Link } from 'dibs-elements/exports/Link';
import { Input } from 'dibs-elements/exports/Input';

import { EMAIL_ERRORS } from '../../../constants/checkoutConstants';
import { openLoginModal } from '../../../actions/checkoutAuthActions';
import { trackFormInteraction } from '../../../helpers/checkoutTracking';
import { checkoutRequestPasswordEmailMutation } from '../../../mutations/checkoutRequestPasswordEmailMutation';

import styles from './styles/email.scss';
import { useAppDispatch } from '../../../reducers/root';

const messages = defineMessages({
    placeholder: {
        id: 'checkout.Email.placeholder',
        defaultMessage: 'Your Email Address*',
    },
});

const Email = ({
    value = '',
    error = '',
    autoFocus = false,
    isBlurred,
    onChange,
    onBlur,
    environment,
    disabled = false,
    rightDecorator,
    rightDecoratorClass,
    showIcons,
    inputRef,
}) => {
    const intl = useIntl();
    const dispatch = useAppDispatch();
    const [requestPasswordSent, setRequestPasswordSent] = useState(false);
    const name = 'email';
    let errorMessage = null;

    const login = (
        <FormattedMessage
            id="checkout.Email.tryAgain"
            defaultMessage="Try another email or {link}."
            values={{
                link: (
                    <Link
                        onClick={e => {
                            e.preventDefault();
                            trackFormInteraction();
                            openLoginModal({ dispatch });
                        }}
                    >
                        <FormattedMessage
                            id="checkout.Email.login"
                            defaultMessage="log in to your account"
                        />
                    </Link>
                ),
            }}
        />
    );

    const createAccount = requestPasswordSent ? (
        <FormattedMessage
            id="checkout.Email.createAccount.confirmation"
            defaultMessage="We emailed you a link to create a password. Once it’s created, you’ll return to checkout."
        />
    ) : (
        <FormattedMessage
            id="checkout.Email.createAccount.prompt"
            defaultMessage="An account with this email exists. {link}"
            values={{
                link: (
                    <Link
                        onClick={e => {
                            e.preventDefault();
                            trackFormInteraction();
                            checkoutRequestPasswordEmailMutation(environment, {
                                email: value,
                                urlRedirect: window.location.pathname,
                            }).then(() => setRequestPasswordSent(true));
                        }}
                    >
                        <FormattedMessage
                            id="checkout.Email.createPassword"
                            defaultMessage="Create a password to continue."
                        />
                    </Link>
                ),
            }}
        />
    );

    if (isBlurred && error) {
        switch (error) {
            case EMAIL_ERRORS.USER_EXISTS:
            case EMAIL_ERRORS.SOCIAL_ACCOUNT_ALREADY_EXISTS:
                errorMessage = (
                    <span>
                        <FormattedMessage
                            id="checkout.Email.EMAIL_ERRORS_EXISTS"
                            defaultMessage="This email address has already been registered."
                        />{' '}
                        {login}
                    </span>
                );
                break;
            case EMAIL_ERRORS.EMAIL_INVALID:
                errorMessage = (
                    <span>
                        <FormattedMessage
                            id="checkout.Email.EMAIL_INVALID"
                            defaultMessage="This email address is invalid."
                        />{' '}
                        {login}
                    </span>
                );
                break;
            case EMAIL_ERRORS.EMAIL_ERRORS_DEFAULT:
                errorMessage = login;
                break;
            case EMAIL_ERRORS.EMAIL_ONLY_USER_EXISTS:
                errorMessage = createAccount;
                break;
            default:
                if (typeof error === 'object' && error.id) {
                    errorMessage = intl.formatMessage(error);
                } else {
                    errorMessage = error;
                }
                break;
        }
    }

    return (
        <div className={styles.wrapper}>
            <Input
                autoFocus={autoFocus}
                type="email"
                name={name}
                value={value}
                pattern="[^ @]*@[^ @]*"
                autoComplete="email"
                placeholder={intl.formatMessage(messages.placeholder)}
                hasAnimatedPlaceholder
                hasValidText={!!value && !error}
                errorMessage={errorMessage}
                dataTn="register-email"
                onChange={event => onChange({ field: name, value: event.target.value })}
                onBlur={event => onBlur({ event, field: name, value: event.target.value })}
                maskForPrivacy
                disabled={disabled}
                rightDecorator={rightDecorator}
                rightDecoratorClass={rightDecoratorClass}
                showIcons={showIcons}
                inputRef={inputRef}
            />
        </div>
    );
};

Email.propTypes = {
    intl: PropTypes.object,
    value: PropTypes.string,
    error: PropTypes.oneOfType([PropTypes.object, PropTypes.node]),
    isBlurred: PropTypes.bool.isRequired,
    onChange: PropTypes.func.isRequired,
    onBlur: PropTypes.func.isRequired,
    environment: PropTypes.object.isRequired,
    disabled: PropTypes.bool,
    rightDecorator: PropTypes.element,
    rightDecoratorClass: PropTypes.string,
    showIcons: PropTypes.bool,
    autoFocus: PropTypes.bool,
    inputRef: PropTypes.func,
};

export default Email;
